import { useEffect, useRef } from "react"

import { useQueries, useQuery } from "@tanstack/react-query"

import { useTranslation } from "@l2r-front/l2r-i18n"

import { l2rApiAxiosInstance as axiosInstance } from "../constants/apis"
import { STATUS_CODE_UNAUTHORIZED } from "../constants/httpStatus"
import { useAlertsDispatchContext } from "../contexts/AlertsContext"
import { getErrorMessage } from "../utils/mutationFeedbacks"

const REFETCH_INTERVAL = 7 * 60 * 1000

export async function queryFn(
    axiosInstance,
    url,
    queryParameters,
    config,
) {
    const response = await axiosInstance.get(url, {
        params: { ...queryParameters },
        ...config,
    })
    return response.data
}

export function useL2rQuery(url, queryKey, queryParameters, config = {}, initialData) {
    const {
        autoRefetch = false,
        fetchConfig,
        onError,
        errorSnackbarMessage,
        ...queryConfig
    } = (config || {})

    const { setSnackbar } = useAlertsDispatchContext()
    const { t } = useTranslation()

    const result = useQuery({
        queryKey,
        queryFn: () => queryFn(axiosInstance, url, queryParameters, fetchConfig),
        ...queryConfig,
        ...(autoRefetch && {
            refetchInterval: REFETCH_INTERVAL,
            refetchIntervalInBackground: true,
            staleTime: REFETCH_INTERVAL,
        }),
        initialData,
    })

    const handledErrorRef = useRef(null)

    useEffect(() => {
        if (!result.isError) {
            handledErrorRef.current = null
            return
        }
        if (result.error === handledErrorRef.current) {
            return
        }
        if (result?.error?.status !== STATUS_CODE_UNAUTHORIZED) {
            handledErrorRef.current = result.error
            onError?.()
            if (errorSnackbarMessage) {
                setSnackbar({
                    label: getErrorMessage(result?.error, config, t),
                    severity: "error",
                    anchorOrigin: {
                        horizontal: "left",
                        vertical: "bottom",
                    },
                    duration: 5000,
                })
            }
        }
    }, [config, result, onError, errorSnackbarMessage, setSnackbar, t])

    return result
}

export function useL2rQueries(queriesData) {
    const queries = queriesData?.map(queryData => {
        const {
            fetchConfig,
            ...queryConfig
        } = (queryData.config || {})
        return {
            queryKey: queryData.queryKey,
            queryFn: () => queryFn(axiosInstance, queryData.url, queryData.queryParameters, fetchConfig),
            ...queryConfig,
        }
    })

    return useQueries({ queries: queries || [] })
}